<template>
  <div class="quiz-carousel-container">
    <div v-if="loading" class="carousel-loading">
      Загрузка...
    </div>
    <div v-else-if="error" class="carousel-error">
      Ошибка загрузки карусели: {{ errorMessage }}
    </div>
    <div v-else-if="carouselData && carouselData.carousel_items && carouselData.carousel_items.length > 0">
      <!-- Debug info - временно включено для отладки -->
      <div 
        class="quiz-carousel-wrapper"
        :class="{ dragging: isDragging }"
        ref="carouselWrapper"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
        @mousedown="handleMouseDown"
        @mousemove="handleMouseMove"
        @mouseup="handleMouseUp"
        @mouseleave="handleMouseUp"
        :style="{ transform: `translateX(${translateX}px)` }"
      >
        <div 
          v-for="item in carouselData.carousel_items" 
          :key="item.id"
          class="quiz-carousel-item"
          :class="imageMeta[item.id]?.isPortrait ? 'portrait' : 'landscape'"
          :style="getItemStyle(item)"
        >
          <div class="quiz-carousel-item-image-wrapper">
            <img 
              :src="getImageUrl(item.image)" 
              :alt="item.title || ''"
              class="quiz-carousel-item-image"
          :class="imageMeta[item.id]?.isPortrait ? 'portrait' : 'landscape'"
              @error="handleImageError"
              @load="(e) => handleImageLoad(e, item)"
            />
            <div class="carousel-item-overlay">
              <h3 class="carousel-item-title" v-if="item.title">
                {{ item.title }}
              </h3>
              <p class="carousel-item-subtitle" v-if="item.subtitle">
                {{ item.subtitle }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="carousel-empty">
      Карусель пуста или данные не загружены
    </div>
  </div>
</template>

<script>
import api from '@/api'

export default {
  name: 'Carousel',
  props: {
    streamSlug: {
      type: String,
      required: true
    },
    carouselDataProp: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      carouselData: null,
      loading: false,
      error: false,
      errorMessage: '',
      translateX: 0,
      itemWidth: 0,
      containerWidth: 0,
      startX: 0,
      currentX: 0,
      isDragging: false,
      isMouseDown: false,
      minTranslate: 0,
      maxTranslate: 0,
      lastItemGap: -100, // небольшой зазор справа для последнего элемента
      imageMeta: {} // { [id]: { isPortrait: boolean, ratio: number } }
    }
  },
  mounted() {
    // Ждём рендера, чтобы ref уже существовал
    this.$nextTick(() => {
      this.calculateItemWidth()
    })
    this.loadCarousel()
    window.addEventListener('resize', this.calculateItemWidth)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.calculateItemWidth)
  },
  watch: {
    streamSlug: {
      immediate: false,
      handler() {
        this.loadCarousel()
      }
    },
    carouselData(newData) {
      if (newData) {
        this.$nextTick(() => {
          this.calculateItemWidth()
          this.resetPosition()
        })
      }
    }
  },
  methods: {
    async loadCarousel() {
      this.loading = true
      this.error = false
      this.errorMessage = ''
      try {
        let data = this.carouselDataProp
        if (!data) {
          data = await api.getCarouselBySlug(this.streamSlug)
        }
        this.carouselData = data
        if (!data || !data.carousel_items || data.carousel_items.length === 0) {
          console.warn('Carousel data is empty')
        }
      } catch (err) {
        console.error('Error loading carousel:', err)
        this.error = true
        this.errorMessage = err.response?.data?.detail || err.message || 'Неизвестная ошибка'
      } finally {
        this.loading = false
      }
    },
    getImageUrl(imagePath) {
      if (!imagePath) return ''
      // Если путь уже полный URL, возвращаем как есть
      if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath
      }
      // Если путь начинается с /, возвращаем как есть (относительно домена)
      if (imagePath.startsWith('/')) {
        return imagePath
      }
      // Иначе добавляем базовый путь к медиа
      return `/media/${imagePath}`
    },
    calculateItemWidth() {
      // Выполняем только после того, как элементы действительно в DOM
      this.$nextTick(() => {
        if (!this.$refs.carouselWrapper) {
          console.warn('Carousel wrapper not found, using default width')
          if (this.itemWidth === 0) {
            this.itemWidth = 300
          }
          return
        }
        
        const parent = this.$refs.carouselWrapper.parentElement
        if (!parent) {
          console.warn('Carousel parent not found, using default width')
          if (this.itemWidth === 0) {
            this.itemWidth = 300
            this.containerWidth = window.innerWidth || 1200
          }
          return
        }
        
        const containerWidth = parent.clientWidth || window.innerWidth
        if (!containerWidth || containerWidth === 0) {
          console.warn('Container width is 0, using fallback')
          const fallbackWidth = Math.min(window.innerWidth || 1200, 1200)
          this.itemWidth = Math.floor(fallbackWidth * 1.2)
          this.containerWidth = fallbackWidth
          return
        }
        
        this.containerWidth = containerWidth
        // Показываем ~1.1-1.2 элемента (обрывок следующего) но не больше контейнера
        const baseWidth = containerWidth * 0.88
        // Ограничим разумно: не меньше 260px и не больше 900px
        this.itemWidth = Math.max(260, Math.min(900, Math.floor(baseWidth)))
        console.log('Item width calculated:', this.itemWidth, 'Container width:', containerWidth)
        
        if (this.carouselData && this.carouselData.carousel_items) {
          const totalWidth = this.itemWidth * this.carouselData.carousel_items.length
          const gap = this.lastItemGap || 0
          // minTranslate с небольшим зазором справа у последнего элемента
          this.minTranslate = -(totalWidth - containerWidth) + gap
          this.maxTranslate = 0
          console.log('Translate bounds:', this.minTranslate, 'to', this.maxTranslate, 'gap:', gap)
        }
      })
    },
    resetPosition() {
      this.translateX = 0
    },
    handleTouchStart(e) {
      this.isDragging = true
      this.startX = e.touches[0].clientX
      this.currentX = this.translateX
    },
    handleTouchMove(e) {
      if (!this.isDragging) return
      
      e.preventDefault()
      const deltaX = e.touches[0].clientX - this.startX
      let newTranslateX = this.currentX + deltaX
      
      // Ограничиваем движение границами
      newTranslateX = Math.max(this.minTranslate, Math.min(this.maxTranslate, newTranslateX))
      
      this.translateX = newTranslateX
    },
    handleTouchEnd() {
      if (!this.isDragging) return
      
      this.isDragging = false
      const moved = this.translateX - this.currentX
      this.snapWithThreshold(moved)
    },
    handleMouseDown(e) {
      this.isMouseDown = true
      this.isDragging = true
      this.startX = e.clientX
      this.currentX = this.translateX
      e.preventDefault()
    },
    handleMouseMove(e) {
      if (!this.isDragging || !this.isMouseDown) return
      
      const deltaX = e.clientX - this.startX
      let newTranslateX = this.currentX + deltaX
      
      // Ограничиваем движение границами
      newTranslateX = Math.max(this.minTranslate, Math.min(this.maxTranslate, newTranslateX))
      
      this.translateX = newTranslateX
    },
    handleMouseUp() {
      if (!this.isDragging) return
      
      this.isDragging = false
      this.isMouseDown = false
      const moved = this.translateX - this.currentX
      this.snapWithThreshold(moved)
    },
    snapWithThreshold(movedDelta) {
      if (!this.carouselData || !this.carouselData.carousel_items.length) return
      if (!this.itemWidth) return

      const itemsCount = this.carouselData.carousel_items.length
      const currentPosition = -this.translateX
      const currentIndex = Math.round(currentPosition / this.itemWidth)

      const threshold = this.itemWidth * 0.15  // 15% ширины карточки — легче пролистывать
      let targetIndex = currentIndex

      if (Math.abs(movedDelta) > threshold) {
        targetIndex = currentIndex + (movedDelta < 0 ? 1 : -1)
      }

      targetIndex = Math.max(0, Math.min(itemsCount - 1, targetIndex))

      const containerWidth = this.containerWidth || this.$refs.carouselWrapper?.parentElement?.clientWidth || window.innerWidth || this.itemWidth
      const centerOffset = Math.max(0, (containerWidth - this.itemWidth) / 2)
      const isLastItem = targetIndex === itemsCount - 1

      // Для всех, кроме последнего, центрируем элемент.
      // Последний фиксируем по правому краю с зазором, который уже учтён в minTranslate.
      let targetPosition = isLastItem
        ? this.minTranslate
        : -(targetIndex * this.itemWidth - centerOffset)

      targetPosition = Math.max(this.minTranslate, Math.min(this.maxTranslate, targetPosition))

      this.animateToPosition(targetPosition)
    },
    animateToPosition(targetPosition) {
      const startPosition = this.translateX
      const distance = targetPosition - startPosition
      const duration = 300
      const startTime = performance.now()
      
      const animate = (currentTime) => {
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / duration, 1)
        
        // Easing function (ease-out)
        const easeProgress = 1 - Math.pow(1 - progress, 3)
        
        this.translateX = startPosition + distance * easeProgress
        
        if (progress < 1) {
          requestAnimationFrame(animate)
        }
      }
      
      requestAnimationFrame(animate)
    },
    handleImageError(e) {
      console.error('Error loading carousel image:', e.target.src)
      // Можно установить placeholder изображение
      e.target.src = '/img/default.jpg'
    },
    handleImageLoad(event, item) {
      const img = event?.target
      if (!img || !item) return

      const width = img.naturalWidth || 1
      const height = img.naturalHeight || 1
      const isPortrait = height > width
      const ratio = width / height // width-to-height, <1 значит портрет

      this.$set(this.imageMeta, item.id, { isPortrait, ratio })
      console.log('Carousel image loaded successfully', { id: item.id, isPortrait, ratio })
    },
    getItemStyle(item) {
      const meta = this.imageMeta[item.id] || {}
      const isPortrait = meta.isPortrait
      const ratio = meta.ratio || (16 / 9) // width / height
      const width = this.itemWidth || 300

      const base = {
        width: `${width}px`,
        minWidth: '260px',
        marginRight: '14px',
        background: 'transparent',
        border: '1px solid rgba(255,255,255,0.2)',
        borderRadius: '16px',
        overflow: 'hidden'
      }

      if (isPortrait) {
        // Высота вычисляется из реального соотношения сторон: width / ratio
        const heightPx = width / ratio
        base.aspectRatio = ratio // (<1)
        base.height = `${Math.min(Math.max(heightPx, 320), 720)}px`
        base.minHeight = '300px'
        base.maxHeight = '720px'
      } else {
        const heightPx = width / ratio
        base.aspectRatio = ratio // (>=1)
        base.height = `${Math.min(Math.max(heightPx, 220), 520)}px`
        base.minHeight = '220px'
        base.maxHeight = '520px'
      }

      return base
    }
  }
}
</script>

<style scoped>
.quiz-carousel-container {
  width: 100%;
  overflow: hidden;
  position: relative;
  min-height: 420px;
  background: transparent;
}

.quiz-carousel-wrapper {
  display: flex;
  will-change: transform;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  min-height: 420px;
  position: relative;
  align-items: stretch;
  padding: 0 12px;
  box-sizing: border-box;
}

.quiz-carousel-wrapper:active,
.quiz-carousel-wrapper.dragging {
  cursor: grabbing;
  transition: none;
}

.quiz-carousel-wrapper:not(.dragging) {
  transition: transform 0.3s ease-out;
}

.quiz-carousel-item {
  flex-shrink: 0;
  position: relative;
  aspect-ratio: 16 / 9;
  height: auto;
  max-height: 480px;
  min-height: 220px;
  margin-right: 14px;
  min-width: 260px;
  background: transparent;
  border: 1px solid rgba(255,255,255,0.2);
  overflow: hidden;
  border-radius: 16px;
}

.quiz-carousel-item-image-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quiz-carousel-item-image {
  width: 100%;
  height: 100%;
  object-position: center;
  display: block;
}

.quiz-carousel-item-image.landscape {
  object-fit: cover;
}

.quiz-carousel-item-image.portrait {
  object-fit: contain;
  max-width: 100%;
  max-height: 100%;
}

.carousel-item-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.4) 50%, transparent 100%);
  padding: 2rem 1.5rem 1.5rem;
  color: #ffffff;
}

.carousel-item-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.carousel-item-subtitle {
  font-size: 1rem;
  margin: 0;
  opacity: 0.9;
  line-height: 1.4;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.carousel-loading,
.carousel-error,
.carousel-empty {
  padding: 2rem;
  text-align: center;
  color: #ffffff;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Скрываем скролл-бар */
.carousel-container::-webkit-scrollbar {
  display: none;
}

.carousel-container {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Адаптивность */
@media (max-width: 768px) {
  .carousel-item {
    height: 300px;
  }
  
  .carousel-item-title {
    font-size: 1.25rem;
  }
  
  .carousel-item-subtitle {
    font-size: 0.9rem;
  }
  
  .carousel-item-overlay {
    padding: 1.5rem 1rem 1rem;
  }
}

@media (max-width: 480px) {
  .carousel-item {
    height: 250px;
  }
  
  .carousel-item-title {
    font-size: 1.1rem;
  }
  
  .carousel-item-subtitle {
    font-size: 0.85rem;
  }
}
</style>
